import { HighlightItem } from "client/Api/TitleBar/GlobalSearch/globalSearch";
import {
	FormattedText,
	Highlight,
	HighlightedText,
	ListItem,
	Space
} from "client/Components";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { NumberFormatter } from "client/Hooks";
import {
	EMPTY_ARRAY,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement } from "react";
import { useHighlights } from "../ResultView/CategoryView/ItemView/useHighlights";
import { IconThumbnail } from "./IconThumbnail";
import { SubTitleSection } from "./SubTitleSection";
import { useGlobalSearchItemActions } from "../GlobalSearchItem/useGlobalSearchItemActions";
import { AppColorViewModel } from "client/Api/Apps/common";

export type CommunityListItemProps = {
	hitHighlights?: readonly HighlightItem[];
	id: number;
	name: string;
	isCurrentUserMember: boolean;
	color: AppColorViewModel;
	memberCount: number;
	itemIndex: number;
	tabIndex: number;
	onRemove?: () => void;
	onExecute: () => void;
};

export const CommunityListItem = (props: CommunityListItemProps): ReactElement => {
	const {
		id,
		name,
		memberCount,
		isCurrentUserMember,
		hitHighlights = EMPTY_ARRAY,
		onRemove,
		itemIndex,
		tabIndex,
		onExecute,
		color
	} = props;

	const formatNumber: NumberFormatter = useHubStatisticsFormatter();
	const highlights: Map<string, Highlight> = useHighlights(hitHighlights);

	const {
		onKeyDown,
		ref
	} = useGlobalSearchItemActions({
		itemIndex,
		onExecute
	});

	return (
		<ListItem
			trackingId={generateTrackingId("community-result-list-item", `|${id}`)}
			noMobileBorderBottom
			tabIndex={tabIndex}
			ref={ref}
			onKeyDown={onKeyDown}
		>
			<ListItem.Content
				thumbnail={
					<IconThumbnail
						icon="users"
						trackingId="thumbnail"
						color={color}
					/>
				}
			>
				<ListItem.Title
					trackingId="title"
				>
					<HighlightedText
						text={name}
						highlight={highlights.get("name")}
					/>
				</ListItem.Title>

				<ListItem.SubTitle>
					<ListItem.SubTitleIcon kind="user-group" />
					<Space />
					<SubTitleSection
						trackingId="members-amount"
						text={
							memberCount === 1
								? localize("_1Member")
								: (
									<FormattedText
										text={localize("_AmountMembers")}
										thingsToReplace={[
											{ what: "{{Amount}}", filler: formatNumber(memberCount) }
										]}
									/>
								)
						}
					/>

					{isCurrentUserMember && (
						<SubTitleSection
							trackingId="is-joined"
							text={localize("_Joined")}
						/>
					)}
				</ListItem.SubTitle>
			</ListItem.Content>
			{isNotNil(onRemove) && (
				<ListItem.Action
					icon="xmark"
					onClick={() => onRemove()}
					bubbleClick={false}
					trackingId="remove"
				/>
			)}
		</ListItem>
	);
};
CommunityListItem.displayName = "CommunityListItem";
